import { type ProgramFocusTypes, SpecificityTypes, type Athletes, ProgramType, type TrainingPlanSettings, specificityValues, defaultForecastOptions, type CalendarSettings } from "@/Common/constants"
import { getDayMedians, mergeForecastInputs, type XSSDetails } from "@/Util/utils"
import { capacityCall, getLibxert } from "@/xert-wasm"
import type { number } from "svelte-i18n"

interface BaseForecastTargets {
    start: Date,
    focus: ProgramFocusTypes,
    specificity_rating: SpecificityTypes,
    currentFocusPower: number,
}

/** Signature entered by the user: ftp (W), hie (J), pp (W) */
export type ManualSignatureTargets = { ftp: number, hie: number, pp: number }
/** Event XSS entered by the user */
export type ManualXssTargets = { xlss: number, xhss: number, xpss: number }

export interface GoalForecastTargets extends BaseForecastTargets {
    program: ProgramType.Goal,
    focus_power: number,
    manualSignature?: ManualSignatureTargets | null,
}

export interface EventForecastTargets extends BaseForecastTargets {
    program: ProgramType.Event,
    xss: number,
    manualXss?: ManualXssTargets | null,
}

export interface RaceForecastTargets extends BaseForecastTargets {
    program: ProgramType.Race,
    xss: number,
    ltl_target: number
    htl_target: number,
    ptl_target: number,
    xlss: number,
    xhss: number,
    xpss: number,
    sfd: number,
    sp: number,
}

export function createForecastInputs<T extends GoalForecastTargets | EventForecastTargets | RaceForecastTargets>(
    targets: T,
    athletes: Athletes, 
    initialInputs, 
    settings: TrainingPlanSettings
) {
    const updatedInputs = initialInputs
    const toUpdate = updatedInputs.days.findIndex(d => d.state?.includes('event_A'))

    // initialInputs is reused between calls, so drop manual targets left over from a previous call
    const { ftp_target, hie_target, pp_target, xlss, xhss, xpss, ...eventDay } = updatedInputs.days[toUpdate]
    const { manualSignature, manualXss, ...baseTargets } = targets as T & { manualSignature?: ManualSignatureTargets | null, manualXss?: ManualXssTargets | null }
    updatedInputs.days[toUpdate] = {
        ...eventDay,
        ...baseTargets,
        title: 'Test Event',
        currentFocusPower: targets.currentFocusPower,
        focus: targets.focus,
        specificity_rating: targets.specificity_rating,
        start: targets.start.toISOString(),
        sp: targets.program !== ProgramType.Race ? specificityValues[targets.specificity_rating] : targets.sp,
        sfd: targets.program !== ProgramType.Race ? athletes[targets.focus].duration : targets.sfd,
        xss: targets.program !== ProgramType.Goal ? targets.xss : 0,
        xss_target: targets.program !== ProgramType.Goal ? targets.xss : 0,
        focus_power: targets.program === ProgramType.Goal ? targets.focus_power : 0
    }

    // The optimizer converts the signature into training load targets
    if (targets.program === ProgramType.Goal && manualSignature) {
        updatedInputs.days[toUpdate] = {
            ...updatedInputs.days[toUpdate],
            ftp_target: manualSignature.ftp,
            hie_target: manualSignature.hie,
            pp_target: manualSignature.pp,
        }
    }

    // The optimizer uses this split as is instead of splitting xss by focus and specificity
    if (targets.program === ProgramType.Event && manualXss) {
        updatedInputs.days[toUpdate] = {
            ...updatedInputs.days[toUpdate],
            xlss: manualXss.xlss,
            xhss: manualXss.xhss,
            xpss: manualXss.xpss,
        }
    }

    if (targets.program === ProgramType.Race) {
        updatedInputs.days[toUpdate] = {
            ...updatedInputs.days[toUpdate],
            xlss_target: targets.xlss,
            xhss_target: targets.xhss,
            xpss_target: targets.xpss,
        }
    }

    // Is this too bulky? Maybe days.state should be an object instead of array?V
    let stateObj = Object.fromEntries(updatedInputs.days[toUpdate].state.map(v=>[v, true]))
    stateObj.target_focus = targets.program == ProgramType.Goal;
    stateObj.target_race = targets.program == ProgramType.Race;
    stateObj.manual_xss = targets.program == ProgramType.Event && !!manualXss;
    updatedInputs.days[toUpdate].state = Object.entries(stateObj).filter(([k, v])=>v).map(([k, v])=>k)

    let forecastSettings = {
        ...defaultForecastOptions,
        recovery: settings.recovery,
        periodization: settings.periodization,
        highRestDays: settings.polarization,
        eventRestDays: settings.taper,
        maxWeeklyHours: settings.maxWeeklyHours,
        minimizeTraining: settings.minimizeTraining,
        difficulty: settings.difficulty,
        difficultyFromTrainingLoad: settings.difficultyFromTrainingLoad,
    }

    return { ...updatedInputs, options: {...forecastSettings} }
}

/**
 * The event's target loads and signature. Given the calendar settings, on the availability the forecast will run with,
 * which a Focus Goal's targets depend on (calculateEvent follows what the days can build).
 */
export async function estimateTargets(forecastInputs: any, cal_settings?: CalendarSettings, trainingMedians?: any){
    // mergeForecastInputs changes the inputs it is given
    const inputs = cal_settings
        ? mergeForecastInputs(structuredClone(forecastInputs), cal_settings.defaults, cal_settings.availabilities, trainingMedians)
        : forecastInputs
    const results = (await getLibxert()).calculate_goals(inputs);
    const event = results.days.find(e => e.state?.includes('event_A'))

    let targetTL: XSSDetails = {
        xlss: event.tls.l,
        xhss: event.tls.h,
        xpss: event.tls.p,
        xss: event.tls.l + event.tls.h + event.tls.p
    }
    let targetSignature: XSSDetails = {
        xlss: event["ftp"],
        xhss: event["hie"],
        xpss: event["pp"],
        xss: null
    }
    let targetXSSBreakdown: XSSDetails = {
        xss: event.xss,
        xlss: event.xlss,
        xhss: event.xhss,
        xpss: event.xpss,
    }

    // Lowest signature reachable at the event (no training until then), ftp/pp in W, hie in J
    const minSignature: ManualSignatureTargets = results.min_signature

    return {targetTL, targetSignature, targetXSSBreakdown, minSignature}
}

export type GoalStatus = "fine" | "approaching" | "beyond"

/** A Focus Goal against its plan's training capacity; focus powers in watts. */
export interface GoalAssessment {
    currentFocusPower: number,
    /** The estimated frontier: the highest focus power the days to the target date can build */
    frontierFocusPower: number,
    /** Highest focus power that is still fine */
    fineFocusPower: number,
    status: GoalStatus,
    /** For a goal that isn't fine, the earliest date it is (null: not within a year) */
    earliestDate: Date | null,
}

/**
 * Assesses a Focus Goal against its plan's training capacity (assess_capacity, the capacity simulator in optimizer.cpp)
 * with the availability the forecast will run with, and logs what the simulator did to the console. Null when it can't
 * be assessed.
 */
export async function assessGoal(forecastInputs: any, cal_settings: CalendarSettings, trainingMedians: any, key?: string): Promise<GoalAssessment | null> {
    const result = await assessCapacity(forecastInputs, cal_settings, trainingMedians, key)
    if (result?.frontier_focus_power == null) return null
    return {
        currentFocusPower: result.current_focus_power,
        frontierFocusPower: result.frontier_focus_power,
        fineFocusPower: result.fine_focus_power,
        status: result.status,
        earliestDate: result.earliest_t ? new Date(result.earliest_t * 1000) : null,
    }
}

/** A Goal with manual signature targets against its plan's training capacity: each target load's status, for the signature
 * value it builds (low for Threshold Power, high for HIE, peak for Peak Power), and overall the worst of them. */
export interface ManualGoalAssessment {
    status: GoalStatus,
    systems: { low: GoalStatus, high: GoalStatus, peak: GoalStatus },
    /** For targets that aren't fine, the earliest date they are (null: not within a year) */
    earliestDate: Date | null,
}

/**
 * Assesses a Goal's manual signature targets against its plan's training capacity (assess_capacity, by the target loads the
 * signature comes to), with the availability the forecast will run with. Null when it can't be assessed.
 */
export async function assessManualGoal(forecastInputs: any, cal_settings: CalendarSettings, trainingMedians: any, key?: string): Promise<ManualGoalAssessment | null> {
    const result = await assessCapacity(forecastInputs, cal_settings, trainingMedians, key)
    if (!result?.system_status) return null
    return {
        status: result.status,
        systems: result.system_status,
        earliestDate: result.earliest_t ? new Date(result.earliest_t * 1000) : null,
    }
}

/** A race's target loads against its plan's training capacity */
export interface RaceAssessment {
    /** Whether the days to the race reach its target loads */
    reachable: boolean,
    /** The lowest share of the target loads the days reach */
    share: number,
    /** When not reachable, the earliest date it is (null: not within a year of the start) */
    earliestDate: Date | null,
}

/**
 * Assesses a race's target loads against its plan's training capacity (assess_capacity), with the availability the
 * forecast will run with. Null when it can't be assessed.
 */
export async function assessRace(forecastInputs: any, cal_settings: CalendarSettings, trainingMedians: any, key?: string): Promise<RaceAssessment | null> {
    const result = await assessCapacity(forecastInputs, cal_settings, trainingMedians, key)
    if (result?.share == null) return null
    return {
        reachable: result.reachable,
        share: result.share,
        earliestDate: result.earliest_t ? new Date(result.earliest_t * 1000) : null,
    }
}

/**
 * The rider's weekly build rate for each system's training load from today's (capacity_rates, the capacity simulator),
 * with the availability and settings the forecast will run with. Null when it can't be found.
 */
export async function capacityRates(forecastInputs: any, cal_settings: CalendarSettings, trainingMedians: any): Promise<{low: number, high: number, peak: number} | null> {
    // mergeForecastInputs changes the inputs it is given
    const merged = mergeForecastInputs(structuredClone(withEveryWeekday(forecastInputs, trainingMedians)), cal_settings.defaults, cal_settings.availabilities, trainingMedians)
    const result = await capacityCall("capacity_rates", { ...merged, options: forecastInputs.options })
    if (result?.error || result?.low == null) {
        console.warn("Training capacity: no weekly build rates", result)
        return null
    }
    console.log("Training capacity: weekly build rates (low/high/peak)", [result.low, result.high, result.peak].map(round1).join(" / "))
    return { low: result.low, high: result.high, peak: result.peak }
}

// A target less than a week away leaves weekdays without a day before it, and the capacity simulator would have no
// availability for them on the days past it (prepareCapacity in optimizer.cpp): a day after the event for each, which
// mergeForecastInputs then gives that weekday's availability.
function withEveryWeekday(inputs: any, trainingMedians: any): any {
    const days: any[] = inputs.days ?? []
    const iEvent = days.findIndex(d => d.state?.includes('event_A'))
    if (iEvent < 0) return inputs
    const weekday = (t: number) => new Date(t*1000).getDay()
    const event = days[iEvent]
    const seen = new Set([...days.slice(0, iEvent).map(d => weekday(d.t)), ...days.filter(d => d.t > event.t).map(d => weekday(d.t))])
    if (seen.size >= 7) return inputs
    // Like the day before the event, or for an event first in the inputs, the event without what makes it one
    const { ltl_target, htl_target, ptl_target, focus_power, ...eventDay } = event
    const template = days[iEvent - 1] ?? eventDay
    const extra = []
    for (let k = 1; k <= 7; ++k) {
        const t = event.t + k*24*3600
        if (seen.has(weekday(t))) continue
        // The weekday's own usual riding, as TrainingForecastJob gives each day, not the day it was copied from
        const medians = trainingMedians ? getDayMedians(trainingMedians, weekday(t)) : null
        extra.push({ ...template, t, state: ['forecast', 'placeholder'], xss: 0, xlss: 0, xhss: 0, xpss: 0, fixed: false,
            ...(medians ? { median_adu: medians.median_adu ?? 1, xss_target: medians.median_xss ?? 60 } : {}) })
    }
    return { ...inputs, days: [...days, ...extra].sort((a, b) => a.t - b.t) }
}

// assess_capacity on the inputs merged with the calendar settings, in the capacity worker, logged to the console. key: the
// caller's, so that a newer call of it replaces one still waiting (null, see capacityCall).
async function assessCapacity(forecastInputs: any, cal_settings: CalendarSettings, trainingMedians: any, key?: string): Promise<any> {
    // mergeForecastInputs changes the inputs it is given
    const merged = mergeForecastInputs(structuredClone(withEveryWeekday(forecastInputs, trainingMedians)), cal_settings.defaults, cal_settings.availabilities, trainingMedians)
    const started = performance.now()
    const result = await capacityCall("assess_capacity", { ...merged, options: forecastInputs.options, assessDays: true }, key)
    const ms = performance.now() - started
    if (result === null) return null
    if (result?.error || result?.share == null) {
        console.warn("Training capacity: no assessment", result)
        return null
    }
    logAssessment(merged, forecastInputs.options, result, ms)
    return result
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const round1 = (x: number) => Math.round(x * 10) / 10

// What went into the capacity simulator and what it did, as one collapsed console group.
function logAssessment(inputs: any, options: any, result: any, ms: number) {
    const days: any[] = inputs.days
    const event = days[result.i_event]
    const gain = (fp: number) => round1(fp - result.current_focus_power)
    const goal = result.frontier_focus_power != null
    console.groupCollapsed(goal
        ? `Training capacity: goal +${gain(result.target_focus_power)} W, frontier +${gain(result.frontier_focus_power)} W, ${result.status} (${Math.round(ms)} ms)`
        : `Training capacity: ${Math.round(100*result.share)}% of the target loads, ${result.status} (${Math.round(ms)} ms)`)
    console.log("Plan:", {
        start: new Date(days[0].t * 1000).toDateString(), target: new Date(event.t * 1000).toDateString(), days: result.i_event,
        focusSeconds: event.sfd, specificity: event.sp,
    })
    console.log("Settings:", {
        recoveryDemands: options.recovery, polarization: options.highRestDays, periodization: options.periodization,
        difficulty: options.difficultyFromTrainingLoad ? "from training load" : options.difficulty, maxWeeklyHours: options.maxWeeklyHours,
    })
    // Availability as each weekday has it most often before the target date: minutes (-1: as usual), or off
    const byWeekday: Record<string, Record<string, number>> = {}
    days.slice(0, result.i_event).forEach(d => {
        const name = DAY_NAMES[new Date(d.t * 1000).getDay()]
        const value = d.availability ? `${d.duration > 0 ? d.duration + " min" : "usual"}${d.fixed_availability ? " fixed" : ""}${d.intensity && d.intensity !== "advisor" ? " " + d.intensity : ""}` : "off"
        byWeekday[name] = byWeekday[name] ?? {}
        byWeekday[name][value] = (byWeekday[name][value] ?? 0) + 1
    })
    console.log("Availability (days of each kind):", byWeekday)
    console.log("Loads (low/high/peak):", {
        start: [days[0].ltl0, days[0].htl0, days[0].ptl0].map(round1),
        reached: [result.reached.ltl, result.reached.htl, result.reached.ptl].map(round1),
        target: [result.target.ltl, result.target.htl, result.target.ptl].map(round1),
    })
    const earliestDate = result.earliest_t ? new Date(result.earliest_t * 1000).toDateString() : (result.unreachable_within_year ? "not within a year" : "-")
    if (goal) console.log("Focus power (W):", {
        current: round1(result.current_focus_power), goal: round1(result.target_focus_power), frontier: round1(result.frontier_focus_power),
        fineUpTo: round1(result.fine_focus_power), status: result.status, earliestDate, peakWeeklyHours: round1(result.peak_weekly_hours),
    })
    else console.log("Target loads:", {
        lowestShare: round1(100*result.share) + "%", status: result.status, earliestDate, peakWeeklyHours: round1(result.peak_weekly_hours),
        ...(result.system_status ? { systems: result.system_status } : {}),
    })
    // The simulated days, a week a row
    const weeks: any[] = []
    ;(result.days ?? []).forEach((d: any, i: number) => {
        const w = weeks[Math.floor(i / 7)] ?? (weeks[Math.floor(i / 7)] = { from: new Date(d.t * 1000).toDateString(), hours: 0, intervalDays: 0, xlss: 0, xhss: 0, xpss: 0 })
        w.hours += d.adu
        w.xlss += d.xlss; w.xhss += d.xhss; w.xpss += d.xpss
        if (d.xhss > 0.5) w.intervalDays += 1
        w.loadsAtStart = w.loadsAtStart ?? [d.ltl0, d.htl0, d.ptl0].map(round1).join(" / ")
    })
    console.table(weeks.map(w => ({ ...w, hours: round1(w.hours), xlss: Math.round(w.xlss), xhss: round1(w.xhss), xpss: round1(w.xpss) })))
    console.groupEnd()
}
